import { AxiosError } from 'axios';

import {
  axiosInstance,
  axiosInstanceForTenantAPI,
  axiosInstanceForTenantSlugOnly,
  axiosInstanceWithSlug,
  axiosInstanceWithSlugForPublicAPI,
} from '../assets/utils/axiosInstance';
import { GCM } from '../assets/utils/cipher';
import { SUBMIT_REPORT } from '../constants/reportConstants';
import {
  ADDITIONAL_INFORMATION,
  CUSTOMER_ADDITIONAL_INFORMATION,
  REPORT_TYPES,
  SUBMIT_REPORT_AUTHENTICATED,
} from '../constants/whistleblowerConstant';
import { CustomFieldValue } from '../interfaces/CustomField';
import { Report, ReportDetail, ReportType } from '../interfaces/Report';

interface ErrorResponse {
  message: string;
}

export interface SubmitReportPayload {
  reportType: string;
  fullName: string;
  isAnonymous: boolean;
  descriptionText: string;
  country: string;
  hasConsent: boolean;
  fileIds: number[];
  customFieldValues?: CustomFieldValue[];
  isInternalSubmission?: boolean;
  fingerprint?: string;
  fingerprintRequestId?: string;
}

export interface SubmitReportForScheduleCallPayload {
  reportType: string;
  country: string;
  fingerprint?: string;
  fingerprintRequestId?: string;
}

interface SubmitAdditionalInformationPayload {
  reportId: string;
  descriptionText: string;
  fileIds: number[];
}

interface SubmitCustomerAdditionalInformationPayload {
  reportId: string;
  fileIds: number[];
}

interface ReportDraftBE {
  id: string;
  report_type: number;
  form_type: number;
  country_verbose: {
    name: string;
    code: string;
  };
  voice_report: {
    file_type: string;
    id: number;
    original_file_name: string;
    url: string;
  };
  full_name: string;
  is_anonymous: boolean;
  description: string;

  files: {
    id: number;
    original_file_name: string;
    file_type: string;
    url: string;
  }[];
  unread_messages_count: number;
  created_at: string;
  updated_at: string;
  report_custom_field_values?: CustomFieldValue[];
}

interface ReportApiResponse {
  results: Report[];
  count: number;
  next: string | null;
  previous: string | null;
}

const reportService = {
  getReport: async (reportId: string): Promise<ReportDetail> => {
    try {
      const response = await axiosInstance().get(`/reports/${reportId}`);
      const { data }: { data: ReportDraftBE } = response;

      // Description in-transit decryption before displaying
      const gcm = new GCM();
      const descriptionText = gcm.decrypt({ cipherText: data.description });

      return {
        ...{
          descriptionText,
          id: data.id,
          reportType: data.report_type,
          country: data.country_verbose?.code,
          fullName: data.full_name || '',
          isAnonymous: data.is_anonymous,
          voiceReport: data.voice_report,
          files:
            data.files && data.files.length > 0
              ? data.files.map(
                  ({ id, original_file_name, file_type, url }) => ({
                    id,
                    url,
                    fileName: original_file_name,
                    fileType: file_type,
                  }),
                )
              : [],
        },
        ...(data.report_custom_field_values &&
          data.report_custom_field_values.length > 0 && {
            customFieldValues: data.report_custom_field_values,
          }),
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  getReportTypes: async (): Promise<ReportType[]> => {
    try {
      const response = await axiosInstanceWithSlug().get(REPORT_TYPES);

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  getReportDrafts: async (urlToFetch: string): Promise<ReportApiResponse> => {
    try {
      const response = await axiosInstance().get(urlToFetch);

      const mappedResults = response.data.results.map((res: ReportDraftBE) => {
        const submissionDate = new Date(res.created_at).toLocaleString(
          import.meta.env.VITE_LOCALE,
          {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          },
        );
        const lastUpdateDate = new Date(res.updated_at).toLocaleString(
          import.meta.env.VITE_LOCALE,
          {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          },
        );

        // Description in-transit decryption before displaying
        const gcm = new GCM();
        const descriptionText = gcm.decrypt({ cipherText: res.description });

        return {
          id: res.id,
          reportType: Number(res.report_type),
          description: descriptionText,
          numMessages: res.unread_messages_count,
          submissionDate,
          lastUpdateDate,
          formType: res.form_type,
        };
      });

      return {
        results: mappedResults,
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  submitReport: async (payload: SubmitReportPayload) => {
    try {
      const {
        reportType,
        fullName,
        isAnonymous,
        descriptionText,
        country,
        hasConsent,
        isInternalSubmission,
        fileIds,
        customFieldValues,
        fingerprint,
        fingerprintRequestId,
      } = payload;

      // Description in-transit encryption preparation
      const gcm = new GCM();
      const cipherDescriptionText = gcm.encrypt({ plainText: descriptionText });

      const body = {
        form_type: 1,
        ...{
          report_type: reportType,
          full_name: fullName,
          is_anonymous: isAnonymous,
          description_text: cipherDescriptionText,
          country,
          has_consent: hasConsent,
          file_ids: fileIds,
          fingerprint: fingerprint,
          fingerprint_request_id: fingerprintRequestId,
        },
        ...(customFieldValues && {
          report_custom_field_values: customFieldValues,
        }),
        ...(isInternalSubmission !== undefined && {
          is_internally_submitted: isInternalSubmission,
        }),
        is_http_cookie_only: true,
      };

      const response = await axiosInstanceWithSlugForPublicAPI().post(
        SUBMIT_REPORT,
        body,
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  submitReportInternal: async (payload: SubmitReportPayload) => {
    try {
      const {
        reportType,
        fullName,
        isAnonymous,
        descriptionText,
        country,
        hasConsent,
        isInternalSubmission,
        fileIds,
        customFieldValues,
        fingerprint,
        fingerprintRequestId,
      } = payload;

      // Description in-transit encryption preparation
      const gcm = new GCM();
      const cipherDescriptionText = gcm.encrypt({ plainText: descriptionText });

      const body = {
        ...{
          report_type: reportType,
          full_name: fullName,
          is_anonymous: isAnonymous,
          description_text: cipherDescriptionText,
          country,
          has_consent: hasConsent,
          file_ids: fileIds,
          fingerprint,
          fingerprintRequestId,
        },
        ...(customFieldValues && {
          report_custom_field_values: customFieldValues,
        }),
        ...(isInternalSubmission !== undefined && {
          is_internally_submitted: isInternalSubmission,
        }),
      };

      const response = await axiosInstanceForTenantSlugOnly().post(
        SUBMIT_REPORT,
        body,
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  submitAdditionalInformation: async (
    payload: SubmitAdditionalInformationPayload,
  ) => {
    try {
      const { reportId, descriptionText, fileIds } = payload;

      const body = {
        report: reportId,
        description_text: descriptionText,
        file_ids: fileIds,
      };

      const response = await axiosInstance().post(ADDITIONAL_INFORMATION, body);

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  submitCustomerAdditionalInformation: async (
    payload: SubmitCustomerAdditionalInformationPayload,
  ) => {
    try {
      const { reportId, fileIds } = payload;
      const body = {
        report: reportId,
        file_ids: fileIds,
      };

      const response = await axiosInstanceForTenantAPI().post(
        CUSTOMER_ADDITIONAL_INFORMATION,
        body,
      );

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },
  submitReportAuthenticated: async (
    payload: SubmitReportPayload & {
      isDraft?: boolean;
    },
    method: 'post' | 'patch',
    reportId?: string | number,
  ) => {
    try {
      const {
        reportType,
        fullName,
        isAnonymous,
        descriptionText,
        country,
        hasConsent,
        fileIds,
        isDraft,
        customFieldValues,
        fingerprint,
        fingerprintRequestId,
      } = payload;
      // Description in-transit encryption preparation
      const gcm = new GCM();
      const cipherDescriptionText = gcm.encrypt({ plainText: descriptionText });

      const body = {
        ...{
          report_type: reportType,
          full_name: fullName,
          is_anonymous: isAnonymous,
          description_text: cipherDescriptionText,
          country,
          has_consent: hasConsent,
          file_ids: fileIds,
          ...(isDraft ? { is_draft: isDraft } : {}),
          fingerprint,
          fingerprintRequestId,
        },
        ...(customFieldValues && {
          report_custom_field_values: customFieldValues,
        }),
        is_http_cookie_only: true,
      };

      const response = await axiosInstanceWithSlug().request({
        url:
          method === 'patch' && reportId
            ? `${SUBMIT_REPORT_AUTHENTICATED}/draft/${reportId}`
            : SUBMIT_REPORT_AUTHENTICATED,
        method,
        data: body,
      });

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  submitReportForScheduledCall: async (
    payload: SubmitReportForScheduleCallPayload,
    method: 'post' | 'patch',
    reportId?: string,
  ) => {
    try {
      const { reportType, country, fingerprint, fingerprintRequestId } =
        payload;
      // Description in-transit encryption preparation
      const gcm = new GCM();
      const cipherDescriptionText = gcm.encrypt({ plainText: '-' });

      const body = {
        report_type: reportType,
        country,
        is_anonymous: false,
        has_consent: true,
        full_name: 'Anonymous',
        description_text: cipherDescriptionText,
        isDraft: false,
        ...(method === 'post' && { file_ids: [] }),
        fingerprint,
        fingerprintRequestId,
      };

      const response = await axiosInstanceWithSlug().request({
        url:
          method === 'patch' && reportId
            ? `${SUBMIT_REPORT_AUTHENTICATED}/draft/${reportId}`
            : SUBMIT_REPORT_AUTHENTICATED,
        method,
        data: body,
      });

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  getVoiceReportTranscription: async (fileId: number) => {
    try {
      const response = await axiosInstance().get<{
        TranscriptionJob?: {
          TranscriptionJobStatus?: string;
          Transcript?: {
            TranscriptFileUri?: string;
          };
        };
      }>(`/report-files/${fileId}/transcription-details`);

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  createDownloadAuditLog: async (reportReference: string, activity: string) => {
    try {
      const response = await axiosInstanceForTenantAPI().post<{
        activity: string;
      }>(`/report/create/download-audit-log/${reportReference}`, {
        activity,
      });

      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw axiosError;
    }
  },

  // Update redacted Description ranges
  getSanitizedReportDetails: async (reportId: string, slug: string) => {
    try {
      const response = await axiosInstanceForTenantAPI(slug).patch(
        `/report/update/${reportId}`,
        {
          sanitize_description: true,
        },
      );
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<ErrorResponse>;
      throw axiosError;
    }
  },
};

export default reportService;
